import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * Empty-state card. Uses a plain padded div inside Card instead of
 * CardContent to avoid the shadcn `pt-0` default (which assumes a
 * CardHeader precedes it and zeroes the top padding when absent).
 *
 * Sizes:
 *   sm  → list-section empty (e.g. "No donations yet")
 *   md  → page-level empty (default)
 *   lg  → hero empty (e.g. "Ready to start your first read-a-thon?")
 *
 * `bare` drops the Card so it can sit inside an existing card's content.
 */
export default function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  size = "md",
  bare = false,
  className
}) {
  const sizing = {
    sm: {
      pad: "p-8 md:p-10",
      iconBox: "h-10 w-10",
      iconSvg: "h-5 w-5",
      title: "font-display text-base font-semibold text-foreground"
    },
    md: {
      pad: "p-10 md:p-14",
      iconBox: "h-12 w-12",
      iconSvg: "h-6 w-6",
      title: "font-display text-lg font-semibold text-foreground"
    },
    lg: {
      pad: "p-12 md:p-16",
      iconBox: "h-16 w-16",
      iconSvg: "h-8 w-8",
      title: "font-display text-2xl md:text-3xl font-semibold text-foreground"
    }
  }[size];

  const body = (
      <div className={cn(bare ? "py-8" : sizing.pad, "text-center space-y-5", bare && className)}>
        {Icon && (
          <div className={cn(
            "mx-auto inline-flex items-center justify-center rounded-full bg-primary/10 text-primary",
            sizing.iconBox
          )}>
            <Icon className={sizing.iconSvg} />
          </div>
        )}
        <div className="space-y-2">
          <p className={sizing.title}>{title}</p>
          {description && (
            <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
              {description}
            </p>
          )}
        </div>
        {action && <div className="pt-2">{action}</div>}
      </div>
  );

  return bare ? body : <Card className={className}>{body}</Card>;
}
